import DSBox from "views/system-design/DSBox";
import SimpleSwitch from "../../../../components/Widgets/SimpleSwitch";
import SimpleBar from "../../../../components/Widgets/SimpleBar";
import SimpleDropdown from "../../../../components/Widgets/SimpleDropdown";
import SimpleTimePicker from "../../../../components/Widgets/SimpleTimePicker";
import { CHIME_MODE, SettingsSectionProps, settingValue } from "./constants";

type QuietTimes = { from: string; until: string };

/**
 * Sound settings for a profile — informed by DeviceModal.tsx's Sound section
 * (lines 795-1108 at last read), not extracted from it. Left out deliberately:
 *  - The pre-592.21 "Hourly Strikes" switch and the old 0-1 volume range
 *    (vs. the modern 0-100 range) — always use the modern ChimeMode dropdown
 *    and 0-100 volumes; an older device ignores fields it doesn't recognize.
 *  - klydosSound/playerVolume's version gate (>= 592.21) — always shown, same
 *    reasoning.
 *  - Master Sound Mixer — read-only, reported BY the device from its actual
 *    audio routing state. A profile pushes settings; it has nothing to push
 *    here, so this is simply not a field a profile can have an opinion on.
 */
const SoundSettings = ({ values, onChange }: SettingsSectionProps) => {
  const quietTimes = settingValue<QuietTimes>(values, "quietTimes");

  return (
    <DSBox>
      <DSBox
        title="Switch chimes sound on/off"
        sx={{ display: "flex", ml: "20px" }}
      >
        <SimpleSwitch
          title="Chimes"
          on={undefined}
          default={settingValue<boolean>(values, "chimes")}
          onClick={(on) => onChange("chimes", on)}
        />
      </DSBox>
      <DSBox title="Chime mode: only chimes / only strikes / both">
        <SimpleDropdown
          title="Chime Mode: "
          list={CHIME_MODE}
          default={settingValue<string>(values, "chimeMode")}
          onSelect={(s) => onChange("chimeMode", s)}
        />
      </DSBox>
      <DSBox title="Chimes volume">
        <SimpleBar
          title="Chimes Volume:"
          max={100}
          default={settingValue<number>(values, "chimesVol")}
          onChange={(e) => onChange("chimesVol", parseInt(e.target.value))}
        />
      </DSBox>
      <DSBox
        title="Switch ticks sound on/off"
        sx={{ display: "flex", ml: "20px" }}
      >
        <SimpleSwitch
          title="Ticks"
          on={undefined}
          default={settingValue<boolean>(values, "ticks")}
          onClick={(on) => onChange("ticks", on)}
        />
      </DSBox>
      <DSBox title="Ticks volume">
        <SimpleBar
          title="Ticks Volume"
          max={100}
          default={settingValue<number>(values, "ticksVol")}
          onChange={(e) => onChange("ticksVol", parseInt(e.target.value))}
        />
      </DSBox>
      <DSBox
        title="Switch klydos sound on/off"
        sx={{ display: "flex", ml: "20px" }}
      >
        <SimpleSwitch
          title="Klydos Sound"
          on={undefined}
          default={settingValue<boolean>(values, "klydosSound")}
          onClick={(on) => onChange("klydosSound", on)}
        />
      </DSBox>
      <DSBox title="Player volume">
        <SimpleBar
          title="Player Volume"
          max={100}
          default={settingValue<number>(values, "playerVolume")}
          onChange={(e) => onChange("playerVolume", parseInt(e.target.value))}
        />
      </DSBox>
      <DSBox
        title="Switch silent times on/off"
        sx={{ display: "flex", ml: "20px" }}
      >
        <SimpleSwitch
          title="Silent times"
          default={settingValue<boolean>(values, "quietTimesOn")}
          onClick={(on) => onChange("quietTimesOn", on)}
          on={undefined}
        />
      </DSBox>
      <DSBox title="Silent Times">
        <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 2, pl: "20px" }}>
          <SimpleTimePicker
            title="From:"
            default={quietTimes.from}
            onChange={(e) =>
              onChange("quietTimes", {
                from: e.target.value,
                until: quietTimes.until,
              })
            }
          />
          <SimpleTimePicker
            title="Until:"
            default={quietTimes.until}
            onChange={(e) =>
              onChange("quietTimes", {
                from: quietTimes.from,
                until: e.target.value,
              })
            }
          />
        </DSBox>
      </DSBox>
    </DSBox>
  );
};

export default SoundSettings;
